import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useFocusTrap } from '../hooks/useFocusTrap';

export default function FunnelPickerModal({ isOpen, onClose, onPick }) {
  const dialogRef = useRef(null);
  useFocusTrap(dialogRef, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#002233]/50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="funnel-picker-title"
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h2 id="funnel-picker-title" className="text-xl font-bold text-[#002233]">Criar nova oportunidade</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-md hover:bg-gray-100 text-gray-500 flex items-center justify-center"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-6">Em qual funil deseja criar?</p>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => onPick('new_names')}
            className="flex-1 p-7 border-2 border-gray-200 rounded-xl text-center transition-all hover:border-[#00DBFF] hover:bg-cyan-50/40 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-cyan-200/50 group"
          >
            <div className="text-4xl mb-3">🆕</div>
            <h3 className="text-base font-bold text-[#002233] group-hover:text-[#00DBFF] transition-colors">
              NEW NAMES
            </h3>
            <p className="text-xs text-gray-500 mt-1">Novo prospect / lead</p>
            <p className="text-[11px] text-gray-400 mt-0.5">sem histórico na base</p>
          </button>

          <button
            type="button"
            onClick={() => onPick('base')}
            className="flex-1 p-7 border-2 border-gray-200 rounded-xl text-center transition-all hover:border-[#A44DFF] hover:bg-purple-50/40 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple-200/50 group"
          >
            <div className="text-4xl mb-3">🏢</div>
            <h3 className="text-base font-bold text-[#002233] group-hover:text-[#A44DFF] transition-colors">
              BASE
            </h3>
            <p className="text-xs text-gray-500 mt-1">Cliente existente</p>
            <p className="text-[11px] text-gray-400 mt-0.5">cross-sell, upgrade, novo módulo</p>
          </button>
        </div>

        <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-gray-500 hover:text-gray-900"
          >
            Cancelar
          </button>
        </div>

        <div className="mt-4 bg-slate-50 rounded-lg p-3 text-[11px] text-gray-500">
          💡 <b>Dica:</b> ao digitar o CNPJ no formulário, o sistema verifica se o cliente já existe na base e sugere o funil correto.
        </div>
      </div>
    </div>
  );
}
